import { useState } from "react";
import { Helmet } from "react-helmet-async";
import SEOHead from "@/components/seo/SEOHead";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  Calculator,
  TrendingUp,
  DollarSign,
  Target,
  ArrowRight,
  CheckCircle2,
  BarChart3,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const formatBRL = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const CalculadoraROI = () => {
  const [vendedores, setVendedores] = useState("");
  const [ticketMedio, setTicketMedio] = useState("");
  const [vendasMes, setVendasMes] = useState("");
  const [taxaConversao, setTaxaConversao] = useState("");

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [saving, setSaving] = useState(false);

  const [resultado, setResultado] = useState<{
    receitaExtraMensal: number;
    receitaExtraAnual: number;
    roi: number;
    taxaAtual: number;
    novaTaxa: number;
    custoEstimado: number;
    planoRecomendado: string;
    custoMensal: number;
    incluiReestruturacao: boolean;
    custoReestruturacao: number;
    roiSemReestruturacao: number;
    custoSemReestruturacao: number;
    // Cenário 2 com 40%
    c2NovaTaxa: number;
    c2ReceitaExtraMensal: number;
    c2ReceitaExtraAnual: number;
    c2CustoEstimado: number;
    c2Roi: number;
  } | null>(null);

  const canCalculate =
    Number(vendedores) > 0 &&
    Number(ticketMedio) > 0 &&
    Number(vendasMes) > 0 &&
    Number(taxaConversao) > 0 &&
    Number(taxaConversao) <= 100;

  const handleCalcular = () => {
    if (!canCalculate) {
      toast({
        title: "Preencha todos os campos",
        description: "Todos os valores devem ser maiores que zero.",
        variant: "destructive",
      });
      return;
    }
    setShowEmailModal(true);
  };

  const calcularEExibir = async () => {
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast({
        title: "E-mail inválido",
        description: "Por favor, insira um e-mail válido.",
        variant: "destructive",
      });
      return;
    }

    const v = Number(vendedores);
    const t = Number(ticketMedio);
    const vm = Number(vendasMes);
    const tc = Number(taxaConversao) / 100;

    // Cenário 1: 20% de melhoria
    const melhoria1 = 0.2;
    const novaTaxa = tc * (1 + melhoria1);
    const vendasExtras = v * vm * (novaTaxa - tc);
    const receitaExtraMensal = vendasExtras * t;
    const receitaExtraAnual = receitaExtraMensal * 12;

    // Lógica de plano: coordenador + vendedores = total de usuários
    const totalUsuarios = v + 1;
    let planoRecomendado: string;
    let custoMensal: number;

    if (totalUsuarios <= 5) {
      planoRecomendado = "Expert";
      custoMensal = 29.90;
    } else {
      planoRecomendado = "Max Pro";
      custoMensal = totalUsuarios * 19.90;
    }

    const custoAnualAssinatura = custoMensal * 12;

    // Reestruturação: sugere quando lucro líquido (receita extra - assinatura) > R$5.000
    const lucroSemReestruturacao = receitaExtraAnual - custoAnualAssinatura;
    const incluiReestruturacao = lucroSemReestruturacao > 5000;
    const custoReestruturacao = incluiReestruturacao ? 5000 : 0;

    const custoSemReestruturacao = custoAnualAssinatura;
    const custoEstimado = custoAnualAssinatura + custoReestruturacao;

    const roiSemReestruturacao = custoSemReestruturacao > 0
      ? ((receitaExtraAnual - custoSemReestruturacao) / custoSemReestruturacao) * 100
      : 0;
    const roi = custoEstimado > 0
      ? ((receitaExtraAnual - custoEstimado) / custoEstimado) * 100
      : 0;

    // Cenário 2: 40% de melhoria (com reestruturação)
    const melhoria2 = 0.4;
    const c2NovaTaxa = tc * (1 + melhoria2);
    const c2VendasExtras = v * vm * (c2NovaTaxa - tc);
    const c2ReceitaExtraMensal = c2VendasExtras * t;
    const c2ReceitaExtraAnual = c2ReceitaExtraMensal * 12;
    const c2CustoEstimado = custoAnualAssinatura + 5000;
    const c2Roi = c2CustoEstimado > 0
      ? ((c2ReceitaExtraAnual - c2CustoEstimado) / c2CustoEstimado) * 100
      : 0;

    setSaving(true);
    try {
      const { error } = await supabase.from("leads_calculadora" as any).insert({
        email: email.trim().substring(0, 255),
        nome: nome.trim().substring(0, 100) || null,
        empresa: empresa.trim().substring(0, 100) || null,
        vendedores: v,
        ticket_medio: t,
        vendas_mes: vm,
        taxa_conversao: Number(taxaConversao),
        roi_calculado: Math.round(roi),
        receita_extra_calculada: Math.round(receitaExtraMensal),
        origem: 'calculadora',
      } as any);

      if (error) {
        console.error("Erro ao salvar lead:", error);
      }
    } catch (err) {
      console.error("Erro ao salvar lead:", err);
    } finally {
      setSaving(false);
    }

    setResultado({
      receitaExtraMensal,
      receitaExtraAnual,
      roi,
      taxaAtual: tc * 100,
      novaTaxa: novaTaxa * 100,
      custoEstimado,
      planoRecomendado,
      custoMensal,
      incluiReestruturacao,
      custoReestruturacao,
      roiSemReestruturacao,
      custoSemReestruturacao,
      c2NovaTaxa: c2NovaTaxa * 100,
      c2ReceitaExtraMensal,
      c2ReceitaExtraAnual,
      c2CustoEstimado,
      c2Roi,
    });
    setShowEmailModal(false);
  };

  const howToSteps = [
    {
      title: "Levante o número de vendedores da sua equipe",
      desc: "Conte quantos vendedores ativos participam do processo comercial. Inclua SDRs, closers e executivos de vendas que terão contato com o treinamento.",
    },
    {
      title: "Identifique o ticket médio das suas vendas",
      desc: "Calcule o valor médio de cada venda fechada. Quanto maior o ticket, mais impactante será a melhoria na taxa de conversão sobre o faturamento total.",
    },
    {
      title: "Meça quantas vendas cada vendedor fecha por mês",
      desc: "Registre a média de vendas concluídas por vendedor. Esse número será a base para projetar o aumento de receita com o treinamento.",
    },
    {
      title: "Defina a taxa de conversão atual da equipe",
      desc: "A taxa de conversão é a porcentagem de leads que se tornam clientes. Treinamentos bem estruturados costumam aumentar essa taxa em 15% a 25%.",
    },
    {
      title: "Aplique a fórmula do ROI líquido",
      desc: "ROI Líquido = (Receita Extra Anual − Investimento) ÷ Investimento × 100. Se positivo, é lucro. Se negativo, é prejuízo. O investimento considera o plano ideal para o tamanho da sua equipe.",
    },
    {
      title: "Use a calculadora abaixo e veja o resultado na hora",
      desc: "Preencha os campos com os dados da sua equipe e descubra instantaneamente quanto sua empresa pode ganhar a mais investindo em treinamento de vendas.",
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "Como Calcular o ROI do Treinamento de Vendas",
    description:
      "Guia passo a passo para calcular o retorno sobre investimento em treinamento comercial e descobrir quanto sua empresa pode faturar a mais.",
    step: howToSteps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.title,
      text: s.desc,
    })),
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Calculadora de ROI de Treinamento de Vendas"
        description="Descubra quanto sua empresa está perdendo sem treinar a equipe comercial. Calcule o ROI do treinamento de vendas gratuitamente e veja o impacto no faturamento."
        keywords="calculadora ROI treinamento vendas, retorno investimento treinamento comercial, ROI vendas, calcular ROI equipe vendas, treinamento vendedores ROI"
        canonicalUrl="https://bianchimbusiness.com.br/calculadora-roi"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <main className="container mx-auto px-4 py-16 max-w-4xl">
        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
            <Calculator className="w-4 h-4 text-primary" />
            <span className="text-sm text-primary font-medium">
              Ferramenta Gratuita
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
            Calculadora de ROI de{" "}
            <span className="text-primary">Treinamento de Vendas</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Descubra quanto sua empresa está perdendo por não treinar a equipe
            comercial. Insira os dados reais do seu time e veja o impacto direto
            no faturamento — em segundos.
          </p>
        </motion.section>

        {/* Conteúdo Editorial - Enumeração para Google */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            Como Calcular o ROI do Treinamento de Vendas
          </h2>
          <ol className="space-y-6">
            {howToSteps.map((step, i) => (
              <li key={i} className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-lg">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-foreground mb-1">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground">{step.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </motion.section>

        {/* Calculadora */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          id="calculadora"
        >
          <Card className="border-primary/20 shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl flex items-center justify-center gap-2">
                <BarChart3 className="w-6 h-6 text-primary" />
                Calcule Agora o ROI da Sua Equipe
              </CardTitle>
              <p className="text-muted-foreground mt-2">
                Preencha com os dados reais do seu time comercial
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="vendedores" className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" />
                    Número de Vendedores
                  </Label>
                  <Input
                    id="vendedores"
                    type="number"
                    min="1"
                    placeholder="Ex: 10"
                    value={vendedores}
                    onChange={(e) => setVendedores(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ticket" className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-primary" />
                    Ticket Médio (R$)
                  </Label>
                  <Input
                    id="ticket"
                    type="number"
                    min="1"
                    placeholder="Ex: 5000"
                    value={ticketMedio}
                    onChange={(e) => setTicketMedio(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendas" className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-primary" />
                    Vendas por Vendedor/Mês
                  </Label>
                  <Input
                    id="vendas"
                    type="number"
                    min="1"
                    placeholder="Ex: 8"
                    value={vendasMes}
                    onChange={(e) => setVendasMes(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="conversao" className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    Taxa de Conversão Atual (%)
                  </Label>
                  <Input
                    id="conversao"
                    type="number"
                    min="1"
                    max="100"
                    placeholder="Ex: 25"
                    value={taxaConversao}
                    onChange={(e) => setTaxaConversao(e.target.value)}
                  />
                </div>
              </div>

              <Button
                onClick={handleCalcular}
                disabled={!canCalculate}
                className="w-full text-lg py-6"
                size="lg"
              >
                <Calculator className="w-5 h-5 mr-2" />
                Calcular Meu ROI
              </Button>
            </CardContent>
          </Card>
        </motion.section>

        {/* Resultado */}
        {resultado && (
          <motion.section
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="mt-12 space-y-8"
          >
            <h2 className="text-2xl font-bold text-center">
              <CheckCircle2 className="w-6 h-6 text-primary inline mr-2" />
              Resultado da Sua Equipe
            </h2>

            {/* Cards principais */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="pt-6 text-center space-y-2">
                  <p className="text-sm text-muted-foreground">Receita Extra Mensal</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatBRL(resultado.receitaExtraMensal)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Taxa: {resultado.taxaAtual.toFixed(1)}% → {resultado.novaTaxa.toFixed(1)}% (+{(resultado.novaTaxa - resultado.taxaAtual).toFixed(2)} p.p.)
                  </p>
                </CardContent>
              </Card>
              <Card className="border-secondary/30 bg-secondary/5">
                <CardContent className="pt-6 text-center space-y-2">
                  <p className="text-sm text-muted-foreground">Receita Extra Anual</p>
                  <p className="text-2xl font-bold text-secondary">
                    {formatBRL(resultado.receitaExtraAnual)}
                  </p>
                  <p className="text-xs text-muted-foreground">projeção de 12 meses</p>
                </CardContent>
              </Card>
            </div>

            {/* Cenário: Apenas Plataforma */}
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Cenário 1: Plano {resultado.planoRecomendado}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Investimento Anual</p>
                    <p className="text-xl font-bold text-foreground">
                      {formatBRL(resultado.custoSemReestruturacao)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatBRL(resultado.custoMensal)}/mês
                      {resultado.planoRecomendado === "Expert"
                        ? " (até 5 usuários)"
                        : ` (${Number(vendedores) + 1} usuários × R$19,90)`}
                    </p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Lucro Líquido Anual</p>
                    <p className={`text-xl font-bold ${resultado.receitaExtraAnual - resultado.custoSemReestruturacao >= 0 ? "text-primary" : "text-destructive"}`}>
                      {formatBRL(resultado.receitaExtraAnual - resultado.custoSemReestruturacao)}
                    </p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">ROI Líquido</p>
                    <p className={`text-xl font-bold ${resultado.roiSemReestruturacao >= 0 ? "text-primary" : "text-destructive"}`}>
                      {resultado.roiSemReestruturacao >= 0 ? "+" : ""}{Math.round(resultado.roiSemReestruturacao)}%
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {resultado.roiSemReestruturacao >= 0 ? "retorno positivo ✓" : "prejuízo ✗"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cenário 2: Com Reestruturação + 40% melhoria */}
            {resultado.incluiReestruturacao && (
              <Card className="border-accent/20 bg-accent/5">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-accent" />
                    Cenário 2: Plano {resultado.planoRecomendado} + Reestruturação Comercial
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Com a reestruturação comercial, a melhoria estimada sobe para <strong>40%</strong> na taxa de conversão.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Taxa de conversão do cenário 2 */}
                  <div className="text-center p-3 rounded-lg bg-accent/10 border border-accent/20">
                    <p className="text-xs text-muted-foreground mb-1">Taxa de Conversão</p>
                    <p className="text-sm font-semibold">
                      {resultado.taxaAtual.toFixed(1)}% → {resultado.c2NovaTaxa.toFixed(1)}% (+{(resultado.c2NovaTaxa - resultado.taxaAtual).toFixed(2)} p.p.)
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground mb-1">Receita Extra Mensal</p>
                      <p className="text-xl font-bold text-accent">
                        {formatBRL(resultado.c2ReceitaExtraMensal)}
                      </p>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground mb-1">Receita Extra Anual</p>
                      <p className="text-xl font-bold text-accent">
                        {formatBRL(resultado.c2ReceitaExtraAnual)}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground mb-1">Investimento Total</p>
                      <p className="text-xl font-bold text-foreground">
                        {formatBRL(resultado.c2CustoEstimado)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatBRL(resultado.custoSemReestruturacao)} assinatura + R$5.000 reestruturação
                      </p>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground mb-1">Lucro Líquido Anual</p>
                      <p className={`text-xl font-bold ${resultado.c2ReceitaExtraAnual - resultado.c2CustoEstimado >= 0 ? "text-primary" : "text-destructive"}`}>
                        {formatBRL(resultado.c2ReceitaExtraAnual - resultado.c2CustoEstimado)}
                      </p>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground mb-1">ROI Líquido</p>
                      <p className={`text-xl font-bold ${resultado.c2Roi >= 0 ? "text-primary" : "text-destructive"}`}>
                        {resultado.c2Roi >= 0 ? "+" : ""}{Math.round(resultado.c2Roi)}%
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {resultado.c2Roi >= 0 ? "retorno positivo ✓" : "prejuízo ✗"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CTA */}
            <Card className="border-primary/20">
              <CardContent className="pt-6 text-center space-y-4">
                <p className="text-muted-foreground">
                  Com o plano <strong>{resultado.planoRecomendado}</strong>, sua equipe pode gerar até{" "}
                  <strong className="text-primary">
                    {formatBRL(resultado.receitaExtraAnual)}
                  </strong>{" "}
                  a mais por ano. Quer começar?
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg">
                    <Link to="/consultoria">
                      Falar com Especialista
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg">
                    <a
                      href="https://wa.me/5516981960018?text=Olá!%20Calculei%20o%20ROI%20do%20treinamento%20e%20quero%20saber%20mais."
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      WhatsApp
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.section>
        )}
      </main>

      <Footer />

      {/* Modal de E-mail */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Quase lá! Insira seu e-mail</DialogTitle>
            <DialogDescription>
              Informe seu e-mail para ver o resultado completo da calculadora.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="lead-email">E-mail *</Label>
              <Input
                id="lead-email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={255}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lead-nome">Nome (opcional)</Label>
              <Input
                id="lead-nome"
                type="text"
                placeholder="Seu nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                maxLength={100}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lead-empresa">Empresa (opcional)</Label>
              <Input
                id="lead-empresa"
                type="text"
                placeholder="Nome da empresa"
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                maxLength={100}
              />
            </div>
            <Button
              onClick={calcularEExibir}
              disabled={saving}
              className="w-full"
              size="lg"
            >
              {saving ? "Calculando..." : "Ver Meu Resultado"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Seus dados estão seguros. Não enviamos spam.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalculadoraROI;
